import Environment, { type ViewportDimensions } from "../util/Environment";
import invertObject from "../util/invertObject";
import Ad from "../gpt/Ad";

const SPANS_ALL_BREAKPOINTS = "all";

function round(number: number) {
    return number > 0 ? Math.floor(number) : Math.ceil(number);
}

type Breakpoints = Record<string, number>;
type InvertedBreakpoints = Record<number, string>;
export type AdSizes = [number, number][];
export type AdSizeMap = Record<string, AdSizes>;

/**
 * Delegate viewability concerns with respect to the viewport and breakpoints
 */
export default class Viewability {

    private _invertedBreakpoints: InvertedBreakpoints;

    constructor(breakpoints: Breakpoints = {}) {
        this._invertedBreakpoints = invertObject(breakpoints);
    }

    /**
     * Gets the current breakpoint of the viewport
     */
    private _currentBreakpoint(): string | undefined {
        const windowWidth = window.innerWidth;
        const invertedBreakpoints = this._invertedBreakpoints;

        const breakpointWidth =
            (Object.keys(invertedBreakpoints) as unknown as (keyof typeof invertedBreakpoints)[])
                .reduce((smallerBreakpoint, largerBreakpoint) =>
                    (windowWidth >= largerBreakpoint ? largerBreakpoint : smallerBreakpoint));

        return this._invertedBreakpoints[breakpointWidth];
    }

    private _isViewable(element: HTMLElement, { height, width }: ViewportDimensions, acceptableOffset = 0) {
        if (!element.offsetParent) {
            return false;
        }

        const { top, bottom, left, right } = element.getBoundingClientRect();
        const viewportTop = 0 - acceptableOffset;
        const viewportBottom = height + acceptableOffset;

        const verticallyViewable = (round(top) >= viewportTop) && (round(bottom) <= viewportBottom);
        const horizontallyViewable = round(left) >= 0 && round(right) <= width;

        return verticallyViewable && horizontallyViewable;
    }

    /**
     * Returns list of ads that are viewable
     * It is important to note that all DOM queries are made in batch and as infrequently as possible
     * Querying the DOM for position info, performing a DOM operation, and performing another query
     * causes the browser to perform a forced layout. This is extremely expensive and should be avoided.
     */
    filter(ads: Ad[] = []): Ad[] {
        const viewportDimensions = Environment.getViewportDimensions();

        return ads.filter((ad) => this.isViewable(viewportDimensions, ad));
    }

    /**
     * returns true when every ad is viewable, false otherwise
     */
    everyViewable(ads: Ad[] = []): boolean {
        const viewportDimensions = Environment.getViewportDimensions();

        return ads.every((ad) => this.isViewable(viewportDimensions, ad));
    }

    /**
     * Check if the ad is in viewport given the viewport dimensions
     * and any offset specified on the ad itself.
     */
    isViewable(viewportDimensions: ViewportDimensions, ad: Ad): boolean {
        let adElement = ad.getElement();

        const viewable = adElement
            ? this._isViewable(adElement, viewportDimensions, ad.getViewportOffset())
            : false;

        adElement = null;

        return viewable;
    }

    /**
     * Gets the sizes for the current viewport
     */
    getSizes(sizes: AdSizeMap | AdSizes): AdSizes {
        if (isAdDimensions(sizes)) {
            return sizes;
        }

        const currentBreakpoint = this._currentBreakpoint();
        const sizesForCurrentBreakpoint =
            currentBreakpoint && sizes[currentBreakpoint] ? sizes[currentBreakpoint] : [];
        const sizesSpanningAllBreakpoints = sizes[SPANS_ALL_BREAKPOINTS] || [];

        return [...sizesForCurrentBreakpoint, ...sizesSpanningAllBreakpoints];
    }

    /**
     * Returns true if sizes object contains mobile-only dimensions
     */
    isMobile(sizes: AdSizeMap | AdSizes): boolean {
        const currentBreakpoint = this._currentBreakpoint();

        return "mobile" === currentBreakpoint && ("mobile" in sizes);
    }

    destroy() {
        // @ts-expect-error ts migration
        this._invertedBreakpoints = null;
    }

}

function isAdDimensions(sizes: AdSizeMap | AdSizes): sizes is AdSizes {
    return Array.isArray(sizes);
}
